import { createSlice } from "@reduxjs/toolkit";
import { PAGELENGTH } from "components/common/ListLayout";
import Api from "services/api";

export const initialState = {
    timesheets: [],
    interpreterTimesheets: [],
    modalTimesheets: [],
    error: null,
    loader: false,
    total: 0,
    is_more: false,
    last_page: 1,
    current_page: 1,
    per_page: PAGELENGTH,
};

export const timesheetSlice = createSlice({
    name: "timesheet",
    initialState,
    reducers: {
        setTimesheets: (state, { payload }) => {
            state.timesheets = payload?.data ?? [];
            state.total = payload?.total ?? 0;
            state.is_more = payload?.is_more ?? false;
            state.last_page = payload?.last_page ?? 1;
            state.current_page = payload?.current_page ?? 1;
            state.per_page = payload?.per_page ?? PAGELENGTH;
        },
        setModalTimesheets: (state, { payload }) => {
            state.modalTimesheets = payload?.data ?? [];
            state.total = payload?.total ?? 0;
            state.is_more = payload?.is_more ?? false;
            state.last_page = payload?.last_page ?? 1;
            state.current_page = payload?.current_page ?? 1;
            state.per_page = payload?.per_page ?? PAGELENGTH;
        },
        setInterpreterTimesheets: (state, { payload }) => {
            state.interpreterTimesheets = payload?.data ?? [];
        },
        setMoreTimesheets: (state, { payload }) => {
            state.timesheets = state.timesheets.concat(payload.data);
            state.total = payload.total;
            state.is_more = payload.is_more;
            state.last_page = payload?.last_page;
            state.current_page = payload?.current_page;
            state.per_page = payload?.per_page;
        },
        clearAllTimesheets: (state) => {
            state.timesheets = [];
            state.error = null;
            state.loader = false;
        },
        setLoader: (state, { payload }) => {
            state.loader = payload;
        },
        setError: (state, { payload }) => {
            state.error = payload;
        },
    },
});

// ✅ Fetch Interpreter Timesheets
export const fetchAdminInterpreterTimesheets =
    (page = 1, params = {}, disableLoader = false, Async = false) =>
        async (dispatch) => {
            if (!disableLoader) dispatch(setLoader(true));
            try {
                const result = await Api.fetchAdminInterpreterTimesheets(page, params);

                if (result.status >= 200 && result.status < 300) {
                    const response = result.data;

                    if (Async) return response;

                    dispatch(
                        setTimesheets({
                            data: response.data,
                            total: response.pagination?.total,
                            is_more: response.pagination?.current_page < response.pagination?.last_page,
                            last_page: response.pagination?.last_page,
                            current_page: response.pagination?.current_page,
                            per_page: response.pagination?.per_page,
                        })
                    );
                } else if (result.status === 422) {
                    if (Async) return [];
                    dispatch(setError(result.data));
                }
            } catch (error) {
                console.error("fetchAdminInterpreterTimesheets error:", error);
                dispatch(setError(error));
            } finally {
                if (!disableLoader) dispatch(setLoader(false));
            }
        };

// Fetch Interpreter Timesheets
export const fetchSingleAdminInterpreterTimesheets =
    (page = 1, params = {}, disableLoader = false, Async = false) =>
        async (dispatch) => {
            if (!disableLoader) dispatch(setLoader(true));
            try {
                const result = await Api.fetchSingleAdminInterpreterTimesheets(page, params);

                if (result.status >= 200 && result.status < 300) {
                    const response = result.data;

                    if (Async) return response;

                    dispatch(
                        setInterpreterTimesheets({
                            data: response.data,
                            total: response.pagination?.total,
                            is_more: response.pagination?.current_page < response.pagination?.last_page,
                            last_page: response.pagination?.last_page,
                            current_page: response.pagination?.current_page,
                            per_page: response.pagination?.per_page,
                        })
                    );
                } else if (result.status === 422) {
                    if (Async) return [];
                    dispatch(setError(result.data));
                }
            } catch (error) {
                console.error("fetchSingleAdminInterpreterTimesheets error:", error);
                dispatch(setError(error));
            } finally {
                if (!disableLoader) dispatch(setLoader(false));
            }
        };

// ✅ Fetch Staff Timesheets (Same logic, different API)
export const fetchStaffTimesheets =
    (page = 1, params = {}, disableLoader = false, Async = false) =>
        async (dispatch) => {
            if (!disableLoader) dispatch(setLoader(true));
            try {
                const result = await Api.fetchStaffTimesheets(page, params);

                if (result.status >= 200 && result.status < 300) {
                    const response = result.data;

                    if (Async) return response;

                    dispatch(
                        setTimesheets({
                            data: response.data,
                            total: response.pagination?.total,
                            is_more: response.pagination?.current_page < response.pagination?.last_page,
                            last_page: response.pagination?.last_page,
                            current_page: response.pagination?.current_page,
                            per_page: response.pagination?.per_page,
                        })
                    );
                } else if (result.status === 422) {
                    if (Async) return [];
                    dispatch(setError(result.data));
                }
            } catch (error) {
                console.error("fetchStaffTimesheets error:", error);
                dispatch(setError(error));
            } finally {
                if (!disableLoader) dispatch(setLoader(false));
            }
        };
export const fetchModalStaffTimesheets =
    (page = 1, params = {}, disableLoader = false, Async = false) =>
        async (dispatch) => {
            if (!disableLoader) dispatch(setLoader(true));
            try {
                const result = await Api.fetchSingleStaffTimesheets(page, params);

                if (result.status >= 200 && result.status < 300) {
                    const response = result.data;

                    if (Async) return response;

                    dispatch(
                        setModalTimesheets({
                            data: response.data,
                            total: response.pagination?.total,
                            is_more: response.pagination?.current_page < response.pagination?.last_page,
                            last_page: response.pagination?.last_page,
                            current_page: response.pagination?.current_page,
                            per_page: response.pagination?.per_page,
                        })
                    );
                } else if (result.status === 422) {
                    if (Async) return [];
                    dispatch(setError(result.data));
                }
            } catch (error) {
                console.error("fetchStaffTimesheets error:", error);
                dispatch(setError(error));
            } finally {
                if (!disableLoader) dispatch(setLoader(false));
            }
        };

export const fetchStaff = (page = 1, params = {}, disableLoader = false, Async = false) =>
    async (dispatch) => {
        try {
            const result = await Api.fetchStaffs(page, params);
            if (result.status >= 200 && result.status < 300) {
                const response = result.data;
                if (Async) return response.data;
            } else {
                if (Async) return [];
            }
        } catch (err) {
            console.error("fetchStaff error:", err);
            if (Async) return [];
        }
    };

export const fetchInterpreters = (page = 1, params = {}, disableLoader = false, Async = false) =>
    async (dispatch) => {
        try {
            const result = await Api.fetchInterpreter(page, params);
            if (result.status >= 200 && result.status < 300) {
                const response = result.data;
                if (Async) return response.data; // 👈 return pure array of interpreters
            } else {
                if (Async) return [];
            }
        } catch (err) {
            console.error("fetchInterpreter error:", err);
            if (Async) return [];
        }
    };


export const { setTimesheets, setMoreTimesheets, clearAllTimesheets, setLoader, setError, setInterpreterTimesheets, setModalTimesheets } =
    timesheetSlice.actions;

export default timesheetSlice.reducer;
export const timesheetSelector = (state) => state.timesheet;
